import { LineItemTemplateType } from '@feature/orders/shared';

/**
 * Class representing an order line item in the application.
 *
 * @class
 * @model
 */
export class OrderLineItem {
	/**
	 * Unique identifier for the order line item.
	 * @note It is optional because it is not present when the line item is previewed.
	 * @type {number | undefined}
	 */
	id?: number;

	/**
	 * Unique identifier for the associated order (optional).
	 * @type {number | undefined}
	 */
	orderId?: number;

	/**
	 * Currency code for the order template's country (optional).
	 * @type {string | undefined}
	 */
	orderTemplateCountryCurrencyCode?: string;

	/**
	 * The unique identifier for the order template associated with this line item.
	 * @type {number}
	 */
	templateId: number;

	/**
	 * Indicates whether the line item belongs to an order template group associated with the payee.
	 * @type {boolean}
	 */
	templateGroupForPayee: boolean;

	/**
	 * Unique identifier for the user associated with this line item (optional).
	 * @type {number | undefined}
	 */
	userId?: number;

	/**
	 * The total amount calculated for the order line item.
	 * @type {number}
	 */
	value: number;

	/**
	 * Indicates whether the template starts with reverse calculation.
	 * @todo ETT-154 Add new column for reverse rate calculation -> Drop?
	 * @type {boolean}
	 */
	templateStartReverseCalculation: boolean;

	/**
	 * The original name of the template associated with this line item.
	 * @type {string}
	 */
	originalTemplateName: string;

	/**
	 * The original group name of the template associated with this line item.
	 * @type {string}
	 */
	originalTemplateGroupName: string;

	/**
	 * The alias for the original template group associated with this line item.
	 * @type {string}
	 */
	originalTemplateGroupAlias: string;

	/**
	 * The original formula associated with the template.
	 * @type {string}
	 */
	originalTemplateFormula: string;

	/**
	 * The plain formula as a string associated with the original template.
	 * @type {string}
	 */
	originalTemplateFormulaPlain: string;

	/**
	 * The type of the original template associated with this line item.
	 * @type {LineItemTemplateType}
	 */
	originalTemplateType: LineItemTemplateType;
}

/**
 * Class representing a group of order line items.
 *
 * @class
 * @model
 */
export class OrderLineItemGroup {
	/**
	 * The name of the order line item group.
	 * @type {string}
	 */
	name: string;

	/**
	 * The alias for the order line item group.
	 * @type {string}
	 */
	alias: string;

	/**
	 * An array of order line items associated with the group.
	 * @type {Array<OrderLineItem>}
	 */
	items: Array<OrderLineItem> = [];
}

/**
 * Class representing a non-performance order line items.
 *
 * @class
 * @model
 */
export class VendorLineItem {
	/**
	 * The description of the vendor line item.
	 * @type {string}
	 */
	description: string;

	/**
	 * The number of hours associated with the vendor line item.
	 * @type {number}
	 */
	quantity: number;

	/**
	 * The rate associated with the vendor line item.
	 * @type {number}
	 */
	rate: number;
}
